import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@dfds-frontend/navigator-icons/icons'

import { useElementWidth } from '../../Hooks/useElementWidth'
import { useThreeDots } from '../../Hooks/useThreeDots'
import { useWindowWidth2 } from '../../Hooks/useWindowWidth2'

interface Props {
  children: React.ReactNode
  className?: string
  dataAttrList: { name: string; value: string }[]
  disabled?: boolean
  fieldName: 'route' | 'vehicle' | 'passenger' | 'currency'
  icon: React.ReactNode
  isLoading: boolean
  isOpen: boolean
  onClick: (clickedOutSide: boolean) => void
  onClose: () => void
  title: string
  triggerRef?: React.Ref<HTMLButtonElement>
  value: any
  visibleRows?: 1 | 2
}

const SelectField: React.FC<Props> = ({
  children,
  className,
  dataAttrList,
  disabled,
  fieldName,
  icon,
  isLoading,
  isOpen,
  onClick,
  onClose,
  title,
  triggerRef,
  value,
  visibleRows,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null as unknown as HTMLDivElement)
  const selectFieldWidth = useElementWidth(ref)
  const dataAttrProps = {} as ComponentProps
  dataAttrList.map((dataAttr) => {
    const key = `data-e2e-${dataAttr?.name}`
    const dataAttrKey = key as keyof ComponentProps
    dataAttrProps[dataAttrKey] = dataAttr.value?.toString() ?? ''
  })
  const keyLoading = `data-e2e-${fieldName}-loading`
  const dataAttrKeyLoading = keyLoading as keyof ComponentProps
  dataAttrProps[dataAttrKeyLoading] = isLoading.toString()

  const { isMobile } = useWindowWidth2()
  const loadingDots = useThreeDots()
  const panelId = `${fieldName}-panel`

  useEffect(() => {
    if (!isOpen || isMobile || !ref?.current) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (ref.current && !ref.current.contains(target)) {
        if (!target.closest('[data-travel-search-child]')) {
          onClick(true)
        }
      }
    }

    window.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/refs
  }, [isOpen, isMobile, ref?.current, onClick])

  return (
    <div
      className="relative h-14.5 focus-within:z-10"
      data-e2e={fieldName}
      data-e2e-disbaled={`${fieldName}-${disabled}`}
      data-e2e-open={isOpen}
      ref={ref}
      {...dataAttrProps}
    >
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="group relative h-full w-full border-0 bg-transparent p-0 text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2! focus-visible:outline-semantic-global-primary-default"
        disabled={disabled}
        id={`${fieldName}-trigger`}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) onClick(false)
            else onClose()
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !isOpen && !disabled) {
            event.preventDefault()
            onClick(false)
          }
        }}
        ref={triggerRef}
        type="button"
      >
        <div
          className={clsx(
            'box-border flex h-full w-full flex-row items-center gap-semantic-global-sm border border-solid border-semantic-global-border-light bg-white px-semantic-global-xs group-hover:bg-semantic-global-background-light-grey',
            className
          )}
          id={`${fieldName}-field`}
        >
          <div className={clsx(disabled ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default')}>{icon}</div>
          <div className="flex w-full flex-col justify-center gap-1 truncate">
            <span
              className={clsx(
                'typography-body-xs-regular select-none',
                disabled ? 'cursor-not-allowed text-semantic-global-disabled-default' : 'cursor-pointer text-semantic-global-text-default'
              )}
            >
              {title}
            </span>
            <span
              className={clsx(
                'notranslate w-full flex-none select-none truncate border-none font-medium leading-none focus:outline-none',
                disabled ? 'text-semantic-global-disabled-default' : 'cursor-pointer text-semantic-global-text-default'
              )}
              data-e2e={`${fieldName}-input`}
              id={`${fieldName}-input`}
              translate="no"
            >{`${isLoading ? loadingDots : value}`}</span>
          </div>
          <div className={clsx('ml-auto', disabled && 'text-semantic-global-disabled-default')}>
            {isOpen ? <ChevronUpIcon size="md" /> : <ChevronDownIcon size="md" />}
          </div>
        </div>
      </button>
      <div
        aria-label={title}
        className="z-[2000] bg-white lg:absolute lg:left-0 lg:mt-semantic-global-xs lg:bg-white lg:shadow-md lg:drop-shadow-xl max-lg:fixed max-lg:bottom-0 max-lg:left-0 max-lg:right-0 max-lg:top-0 max-lg:w-full"
        hidden={!isOpen}
        id={panelId}
        role="dialog"
        style={{ minWidth: selectFieldWidth }}
      >
        <div className="flex h-full w-full flex-col">
          <div
            className="flex h-[64px] min-h-[64px] flex-row items-center bg-semantic-global-background-white pl-semantic-global-md lg:hidden"
            id="header-content"
          >
            <label className="max-w-[350px] typography-heading-md text-semantic-global-text-default">{title}</label>
            <button
              aria-label={`${t('CLOSE')} ${title}`}
              className={clsx(
                'ml-auto flex h-full w-12 items-center justify-center border-0 bg-transparent text-semantic-global-text-default focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-semantic-global-primary-default',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onClick(true)
                }
              }}
              type="button"
            >
              <CloseIcon size="md" />
            </button>
          </div>
          <div className="border-0 border-b border-solid border-gray-300 lg:hidden" />
          <div
            className={`w-full bg-semantic-global-background-white max-lg:overflow-y-scroll ${fieldName !== 'route' && visibleRows === 1 && 'max-lg:h-[calc(100%-150px)]'} ${fieldName !== 'route' && visibleRows === 2 && 'max-lg:h-[calc(100%-170px)]'}`}
            id="main-content"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectField

interface ComponentProps {
  dataAttrName: string
  dataAttrValue: string
}
